import { useAppSelector, useAppDispatch } from "../redux/hooks";
import {useState, useCallback, useEffect, useRef, memo } from "react";
import { useNavigate } from "react-router";
import { useAppNavigate } from "../hooks/useAppNavigate";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import widgetRegistry, { WidgetType } from "../widgets/widgetRegistry";
import { createDefaultWidgetData } from "../utils/widgetDefaults";
import {
  updateWidgetData,
  updateSiteContentRequest,
  addWidgetSection,
  deleteWidgetSection,
} from "../redux/slices/siteContentSlice";
import { current } from '@reduxjs/toolkit';
import { toggleEditMode } from "../redux/slices/editModeSlice";
import { toggleNavEditMode } from "../redux/slices/navEditModeSlice";
import { toggleFooterEditMode } from "../redux/slices/footerEditModeSlice";
import { addToCart, openCart } from "../redux/slices/cartSlice";
import { createCartItem } from "../utils/cartUtils";
import { shouldShowSpiceLevel } from "../utils/spiceUtils";
import { filterEnabledModifiers, isModifierRequired } from "../utils/modifierUtils";
import { EditSaveButton, WidgetSelectionModal } from "../components";
import { AddToCartModal } from "../components/AddToCartModal";
import { useMenuAndComboData } from "../hooks/useMenuAndComboData";
import { useWidgetDataUpdater } from "../hooks/useWidgetDataUpdater";
import { shouldShowAddDeleteControls } from "../utils/widgetVisibilityUtils";

// ---------------- Widget Renderer ----------------
const WidgetRenderer = memo(function WidgetRenderer({
  widget,
  index,
  isEditing,
  onUpdate,
  onNavClick,
  onLogoClick,
  onBannerButtonClick,
  onAddToCart,
}: {
  widget: any;
  index: number;
  isEditing: boolean;
  onUpdate: (newData: any) => void;
  onNavClick?: (item: any) => void;
  onLogoClick?: () => void;
  onBannerButtonClick?: (button: any, index: number) => void;
  onAddToCart?: (item: any) => void;
}) {
  const { type, data } = widget;
  
  if (!type) {
    return (
      <div
        key={`fallback-${index}`}
        className="p-8 bg-gray-100 border border-gray-300 rounded-lg text-center m-4"
      >
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Widget Not Found</h3>
        <p className="text-gray-600 mb-4">Widget type is missing</p>
        <details className="text-left">
          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
            Show widget data
          </summary>
          <pre className="mt-2 p-2 bg-gray-200 rounded text-xs overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  // Use widget registry directly for lazy loading
  const LazyComponent = widgetRegistry[type as WidgetType];

  if (!LazyComponent) {
    return (
      <div
        key={`fallback-${index}`}
        className="p-8 bg-gray-100 border border-gray-300 rounded-lg text-center m-4"
      >
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Widget Not Found</h3>
        <p className="text-gray-600 mb-4">Type: {type}</p>
        <details className="text-left">
          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
            Show widget data
          </summary>
          <pre className="mt-2 p-2 bg-gray-200 rounded text-xs overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  return (
    <div key={`widget-${type}-${index}`} className="w-full">
      {/* <Suspense
        fallback={
          <div className="w-full flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-3 border-orange-500 border-t-transparent rounded-full mx-auto" />
            </div>
          </div>
        }
      > */}
        <LazyComponent
          data={data}
          isEditing={isEditing}
          onUpdate={onUpdate}
          onChange={onUpdate}
          onNavClick={onNavClick}
          onLogoClick={onLogoClick}
          onBannerButtonClick={onBannerButtonClick}
          onAddToCart={onAddToCart}
          onComboClick={(combo: any) => {
            // Handle combo click - navigate to combo detail page
            if (!isEditing) {
              // You can add navigation logic here if needed
            }
          }}
          onItemClick={(item: any) => {
            // Handle popular item click - navigate to item detail page
            if (!isEditing) {
              // You can add navigation logic here if needed
            }
          }}
          styles={{ wrapper: { minHeight: "100vh" } }}
        />
      {/* </Suspense> */}
    </div>
  );
});

// ---------------- Home Page ----------------
export default function HomePage() {
  const { data: siteContent, fetchLoading, updateLoading } = useAppSelector((state) => state?.siteContent);
  const {isEditMode} = useAppSelector((state) => state?.editMode);
  const { isEditControl } = useAppSelector((state) => state?.editControl);
  const isNavEditMode = useAppSelector((state) => state?.navEditMode?.isNavEditMode);
  const isFooterEditMode = useAppSelector((state) => state?.footerEditMode?.isFooterEditMode);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const appNavigate = useAppNavigate();

  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [pendingAddIndex, setPendingAddIndex] = useState<number | undefined>(undefined);
  const [modalItem, setModalItem] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isSaving = useRef(false);

  // 🔄 Use custom hooks for data management
  const { menuData, comboData, menuLoading, comboLoading } = useMenuAndComboData();
  
  // 🔄 Use custom hook for widget data updates
  useWidgetDataUpdater({
    siteContent,
    menuData,
    comboData,
    menuLoading,
    comboLoading,
  });

  // 🚀 Use processed widgets directly from Redux state
  const originalWidgets = siteContent?.widgets ?? [];
  const allHomeWidgets = siteContent?.homeWidgets ?? [];
   const widgets = allHomeWidgets.filter((widget: any) => 
    !widget?.type?.toLowerCase().includes("footer")
  );
  console.log('widgets',widgets)
  const hasHomeWidgets = widgets.length > 0;
  const isReady = !fetchLoading && siteContent !== null;


  // Create index mapping between filtered and original arrays
  const getOriginalIndex = useCallback((displayIndex: number, allWidgets: any[], displayWidgets: any[]) => {
    const displayWidget = displayWidgets[displayIndex];
    // Find by widget type and position, since object references might differ
    return allWidgets.findIndex((widget, index) => 
      widget.type === displayWidget.type && 
      JSON.stringify(widget.data) === JSON.stringify(widget.data)
    );
  }, []);


  // 🔧 Update widget data
  const handleWidgetUpdate = useCallback(
    (displayIndex: number, newData: any) => {
      const originalIndex = getOriginalIndex(displayIndex, originalWidgets, widgets);
      
      // Safely unwrap any Immer proxies
      let cleanData;
      try {
        cleanData = current(newData);
      } catch (error) {
        // If current() fails, it's already a plain object
        cleanData = newData;
      }
      dispatch(updateWidgetData({ index: originalIndex, data: cleanData }));
    },
    [dispatch, originalWidgets, widgets, getOriginalIndex]
  );

  // 💾 Save changes - Unified save that coordinates with nav and footer edit modes
  const handleSaveChanges = useCallback(() => {
    if (siteContent && !fetchLoading) {
      isSaving.current = true;
      
      // Toggle off nav edit mode if it's active
      if (isNavEditMode) {
        dispatch(toggleNavEditMode());
      }
      
      // Toggle off footer edit mode if it's active
      if (isFooterEditMode) {
        dispatch(toggleFooterEditMode());
      }
      
      // Save all changes
      dispatch(updateSiteContentRequest());
    }
  }, [dispatch, siteContent, fetchLoading, isNavEditMode, isFooterEditMode]);

  // ✏️ Toggle edit mode
  const handleEditModeToggle = useCallback(() => {
    if (isEditMode) {
      handleSaveChanges();
    } else {
      dispatch(toggleEditMode());
    }
  }, [isEditMode, handleSaveChanges, dispatch]);

  // ➕ Show widget selection modal
  const handleShowAddModal = useCallback((afterIndex?: number) => {
    setPendingAddIndex(afterIndex);
    setShowWidgetModal(true);
  }, []);

  // ✅ Handle widget selection from modal
  const handleSelectWidget = useCallback((widgetType: string) => {
    const widgetData = createDefaultWidgetData(widgetType);
    const newWidget = {
      type: widgetType,
      data: widgetData,
    };
    
    // Convert display index to original index if needed
    const originalAfterIndex = pendingAddIndex !== undefined ? 
      getOriginalIndex(pendingAddIndex, originalWidgets, widgets) : undefined;
    
    dispatch(addWidgetSection({ widget: newWidget, afterIndex: originalAfterIndex }));
    setShowWidgetModal(false);
    setPendingAddIndex(undefined);
  }, [dispatch, pendingAddIndex, originalWidgets, widgets, getOriginalIndex]);

  // ❌ Close modal
  const handleCloseModal = useCallback(() => {
    setShowWidgetModal(false);
    setPendingAddIndex(undefined);
  }, []);

  // 🗑️ Delete section
  const handleDeleteSection = useCallback((displayIndex: number) => {
    const originalIndex = getOriginalIndex(displayIndex, originalWidgets, widgets);
    const nonNavCount = originalWidgets.filter((w: any) => !w.type.toLowerCase().includes('nav')).length;
    
    if (nonNavCount > 1) { // Prevent deleting the last non-nav widget
      dispatch(deleteWidgetSection(originalIndex));
    }
  }, [dispatch, originalWidgets, widgets, getOriginalIndex]);

  // 🧭 Navigation handlers
  const handleNavClick = useCallback((item: any) => {
    // Don't navigate if in edit mode
    if (isEditMode) return;

    // Map navigation URLs to router paths
    const urlToPathMap: { [key: string]: string } = {
      '/': '/',
      '/home': '/',
      '/menu': '/menu',
      '/contact': '/contact',
      '/blog': '/blog',
      '/our-story': '/our-story',
      '/story': '/our-story',
      '/gallery': '/gallery',
      '/events': '/events',
      '/testimonials': '/testimonials',
    };

    const path = urlToPathMap[item.url] || item.url;

    // Handle external URLs
    if (item.url.startsWith('http://') || item.url.startsWith('https://')) {
      window.open(item.url, '_blank');
      return;
    }

    // Handle internal navigation with mode awareness
    appNavigate(path);
  }, [appNavigate, isEditMode]);

  const handleLogoClick = useCallback(() => {
    // Don't navigate if in edit mode
    if (isEditMode) return;

    // Navigate to home page with mode awareness
    appNavigate('/');
  }, [appNavigate, isEditMode]);

  // 🎯 Banner button click handler - navigate to menu page
  const handleBannerButtonClick = useCallback((button: any, index: number) => {
    // Don't navigate if in edit mode
    if (isEditMode) return;

    // Navigate to menu page with mode awareness
    appNavigate('/menu');
  }, [appNavigate, isEditMode]);

  // 🛒 Cart functionality - Check if item needs customization (exact copy from MenuPage)
  const needsCustomization = useCallback((item: any): boolean => {
    const hasSpiceLevel = shouldShowSpiceLevel(item);
    const enabledModifiers = filterEnabledModifiers(item.modifiers_list || []);
    const hasRequiredModifiers = enabledModifiers.some(modifier => isModifierRequired(modifier));
    return hasSpiceLevel || hasRequiredModifiers;
  }, []);

  // 🛒 Main add to cart handler - handles both menu items and combo items
  const handleAddToCart = useCallback((item: any) => {
    // Check if this is a combo item (has combo_id and title) or regular menu item (has pk_id and name)
    const isComboItem = item.combo_id && item.title;
    
    if (isComboItem) {
      // Handle combo items - combos don't need customization, add directly to cart
      const parsedPrice = typeof item.combo_price === "string"
        ? parseFloat(item.combo_price.replace(/[^0-9.-]+/g, ""))
        : item.combo_price;
      const comboPrice = isNaN(parsedPrice) ? 0 : parsedPrice;
      
      const cartItem = {
        item: {
          ...item,
          pk_id: item.combo_id, // Use combo_id as pk_id for cart compatibility
          name: item.title, // Use title as name for cart compatibility
          price: comboPrice.toString(), // Ensure price is string for consistency
        },
        selected: {
          spice: null,
          modifiers: [],
        },
        totalPrice: comboPrice,
        cartItemId: `${item.combo_id}-combo-${Date.now()}`,
      };
      
      dispatch(addToCart(cartItem));
      dispatch(openCart()); // Auto-open cart
    } else {
      // Handle regular menu items
      if (needsCustomization(item)) {
        // Open modal for customization
        setModalItem(item);
        setIsModalOpen(true);
      } else {
        // Add directly to cart for simple items
        const cartItem = {
          item,
          selected: {
            spice: null,
            modifiers: [],
          },
          totalPrice: parseFloat(item.price),
          cartItemId: `${item.pk_id}-basic-${Date.now()}`,
        };
        
        dispatch(addToCart(cartItem));
        dispatch(openCart()); // Auto-open cart
      }
    }
  }, [dispatch, needsCustomization]);

  // 🛒 Modal close handler
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setModalItem(null);
  }, []);

  // 🛒 Modal add to cart handler (exact copy from MenuPage)
  const handleModalAddToCart = useCallback((
    item: any, 
    quantity: number, 
    spiceLevel: number | null, 
    modifiers: any[]
  ) => {
    const cartItem = createCartItem(item, spiceLevel, modifiers, quantity);
    dispatch(addToCart(cartItem));
    // Close modal and open cart
    setIsModalOpen(false);
    setModalItem(null);
    dispatch(openCart());
  }, [dispatch]);

  // 🔄 Exit edit mode after save is completed
  useEffect(() => {
    if (isSaving.current && !updateLoading && !fetchLoading) {
      dispatch(toggleEditMode());
      isSaving.current = false;
    }
  }, [updateLoading, fetchLoading, dispatch]);

  // 🚫 Early returns after all hooks
  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-3 border-orange-500 border-t-transparent rounded-full mx-auto" />
        </div>
      </div>
    );
  }

  if (!hasHomeWidgets) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 font-medium mb-4">No home widgets available</p>
          {isEditMode && (
            <button
              onClick={() => handleShowAddModal()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Your First Widget
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="homepage w-full min-h-screen relative">
    
      {/* Show EditSaveButton when isEditControl is true OR when in edit mode */}
      {(isEditControl || isEditMode) && (
        <EditSaveButton
          isEditMode={isEditMode}
          onToggle={handleEditModeToggle}
          disabled={updateLoading || fetchLoading}
          position="fixed"
          size="lg"
          variant="red"
          ariaLabel={isEditMode ? "Save changes to page" : "Edit page content"}
        />
      )}

     


      {/* Render Widgets */}
      {widgets.map((widget: any, index: number) => (
        <div key={`widget-container-${index}`} className="relative">
          <WidgetRenderer
            widget={widget}
            index={index}
            isEditing={isEditMode}
            onUpdate={(newData) => handleWidgetUpdate(index, newData)}
            onNavClick={handleNavClick}
            onLogoClick={handleLogoClick}
            onBannerButtonClick={handleBannerButtonClick}
            onAddToCart={handleAddToCart}
          />

        
          {/* {isEditMode && shouldShowAddDeleteControls(widget, isEditMode) && (
            <div className="flex justify-center gap-6 py-4 bg-gray-50 border-t border-b border-gray-200">
              <button
                onClick={() => handleShowAddModal(index)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-lg hover:shadow-xl group"
                title="Add section after this one"
              >
                <FiPlus className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span className="font-medium text-sm">Add Section</span>
              </button>

              {widgets.length > 1 && (
                <button
                  onClick={() => handleDeleteSection(index)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl group"
                  title="Delete this section"
                >
                  <FiTrash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-medium text-sm">Delete Section</span>
                </button>
              )}
            </div>
          )} */}
        </div>
      ))}


      {/* Widget Selection Modal */}
      {/* <WidgetSelectionModal
        isOpen={showWidgetModal}
        onClose={handleCloseModal}
        onSelectWidget={handleSelectWidget}
      /> */}
      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={isModalOpen}
        item={modalItem}
        onClose={handleModalClose}
        onAddToCart={handleModalAddToCart}
      />
    </div>
  );
}
